import { motion, useScroll, useTransform } from 'framer-motion';
import { useRef } from 'react';
import { Shield, Globe, Truck, Award, Clock, Users } from 'lucide-react';

const LOGISTICS_BG = 'https://images.unsplash.com/photo-1759272548470-d0686d071036?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBoaWdoJTIwdGVjaCUyMHdhcmVob3VzZSUyMGxvZ2lzdGljcyUyMGdsb2JhbCUyMHRyYWRlfGVufDB8fHx8MTc3MDE3MzI5OHww&ixlib=rb-4.1.0&q=85';

const reasons = [
  { icon: Shield, title: 'Quality Assured', description: 'We deal exclusively with brand-new, sealed products in their original retail and master boxes — no refurbished or grey-market goods' },
  { icon: Globe, title: 'Global Reach', description: 'Reliable international shipping across our supply network, with established logistics partnerships to ensure timely delivery' },
  { icon: Truck, title: 'Fast Delivery', description: 'Express and priority shipping options available on all orders, with full tracking from warehouse to your door' },
  { icon: Award, title: 'Certified Partners', description: 'We work with Authorised Distributors for all major brands globally, ensuring genuine products and full manufacturer warranties' },
  { icon: Clock, title: 'Around-The-Clock Support', description: 'Our dedicated team provides round-the-clock assistance, so you always have a point of contact when you need one' },
  { icon: Users, title: 'Dedicated Account Management', description: 'Every partner is assigned a specialist account manager who understands your business and can tailor solutions accordingly' }
];

export const WhyChooseUs = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start']
  });

  const y = useTransform(scrollYProgress, [0, 1], ['0%', '20%']);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } }
  };

  return (
    <section ref={ref} className="relative min-h-[600px] overflow-hidden" data-testid="why-choose-us-section">
      {/* Parallax Background */}
      <motion.div className="absolute inset-0 z-0" style={{ y }}>
        <div className="absolute inset-0 bg-slate-900/85 z-10" />
        <img
          src={LOGISTICS_BG}
          alt="Global Logistics"
          className="w-full h-full object-cover"
        />
      </motion.div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-20 py-24 md:py-32">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="inline-block px-4 py-2 rounded-full bg-white/10 text-[#0096D6] font-manrope text-sm font-medium mb-4">
            Our Commitment
          </span>
          <h2 className="font-outfit text-4xl md:text-5xl font-bold text-white mb-4">
            Why Partner With Us
          </h2>
          <p className="font-manrope text-white/70 text-lg max-w-2xl mx-auto">
            We combine industry expertise with exceptional service to deliver unparalleled value to our partners.
          </p>
        </motion.div>

        {/* Reasons Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {reasons.map((reason, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              className="group p-6 rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10 hover:bg-white/10 hover:border-[#0096D6]/30 transition-all duration-500"
              data-testid={`reason-card-${index}`}
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#0096D6]/20 flex items-center justify-center group-hover:bg-[#0096D6] transition-colors duration-500">
                  <reason.icon className="w-6 h-6 text-[#0096D6] group-hover:text-white transition-colors duration-500" />
                </div>
                <div>
                  <h3 className="font-outfit text-lg font-semibold text-white mb-1">
                    {reason.title}
                  </h3>
                  <p className="font-manrope text-white/60 text-sm">
                    {reason.description}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
